import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRight, ArrowDown } from 'lucide-react';
import { SplitWords, FadeSlideIn } from './AnimatedText';
import { TEST_IDS } from '@/constants/testIds';

const HERO_IMAGE = 'https://customer-assets.emergentagent.com/job_f54f3a20-4df8-4151-89bb-3d96c747d48f/artifacts/haz0ueek_240920_SARAH_78A7379_F.webp';
const CLIENT_NAMES = ['Preacher', 'Milk Makeup', 'The Modern House', 'Toogood', 'Zip'];

export default function HeroSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start start', 'end start'],
  });

  const imageY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const imageScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);
  const contentY = useTransform(scrollYProgress, [0, 0.8], [0, -30]);
  const contentOpacity = useTransform(scrollYProgress, [0.4, 0.85], [1, 0]);

  const scrollToMethod = (e) => {
    e.preventDefault();
    document.querySelector('#method')?.scrollIntoView({ behavior: 'smooth' });
  };
  const scrollToCta = (e) => {
    e.preventDefault();
    document.querySelector('#cta')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      ref={sectionRef}
      id="hero"
      data-testid={TEST_IDS.heroSection}
      className="min-h-screen bg-[#F7F5F2] relative overflow-hidden"
    >
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-screen">
        {/* Left Content */}
        <motion.div
          className="col-span-1 lg:col-span-7 flex flex-col justify-center px-6 md:px-12 lg:px-20 py-28 lg:pt-28 lg:pb-12 order-2 lg:order-1 relative z-10"
          style={{ y: contentY, opacity: contentOpacity }}
        >
          {/* Decorative floating element */}
          <div className="absolute top-20 right-10 w-16 h-16 border border-[#C9A96E]/5 rounded-full hidden xl:block" />

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <p
              data-testid={TEST_IDS.heroOverline}
              className="text-xs md:text-sm uppercase tracking-[0.25em] text-[#C9A96E] font-sans font-semibold mb-6"
            >
              <motion.span
                initial={{ width: 0 }}
                animate={{ width: 40 }}
                transition={{ duration: 0.8, delay: 0.5 }}
                className="inline-block h-px bg-[#C9A96E] mr-4 align-middle"
              />
              Luminous Coaching
            </p>
          </motion.div>

          <h1
            data-testid={TEST_IDS.heroHeadline}
            className="font-serif text-4xl sm:text-5xl lg:text-[4.2rem] xl:text-[5rem] leading-[1.08] tracking-tighter text-[#1B2A4A] mb-8 max-w-2xl"
          >
            <SplitWords delay={0.4} immediate>
              From the Chairman's Office to the Heart of Your Team
            </SplitWords>
          </h1>

          <FadeSlideIn delay={1.2}>
            <p
              data-testid={TEST_IDS.heroSubheadline}
              className="text-base md:text-lg text-[#2D2D2D] font-sans leading-relaxed mb-10 max-w-xl"
            >
              Sarah Watson, former Chairman of BBH New York — helps creative founders and leaders turn burnout and dysfunction into clarity and breakthrough.
            </p>
          </FadeSlideIn>

          <FadeSlideIn delay={1.5}>
            <div className="flex flex-col sm:flex-row gap-4 mb-14">
              <motion.a
                href="#cta"
                data-testid={TEST_IDS.heroCtaPrimary}
                onClick={scrollToCta}
                className="bg-[#D4853A] text-white px-8 py-4 uppercase text-sm tracking-widest font-sans font-medium hover:bg-[#1B2A4A] transition-colors duration-300 inline-flex items-center justify-center gap-2 relative"
                whileHover={{ scale: 1.02, x: 4 }}
                whileTap={{ scale: 0.98 }}
              >
                Book a Conversation
                <ArrowRight className="w-4 h-4" />
              </motion.a>
              <motion.a
                href="#method"
                data-testid={TEST_IDS.heroCtaSecondary}
                onClick={scrollToMethod}
                className="border border-[#1B2A4A] text-[#1B2A4A] px-8 py-4 uppercase text-sm tracking-widest font-sans font-medium hover:bg-[#1B2A4A] hover:text-[#F7F5F2] transition-all duration-300 inline-flex items-center justify-center gap-2"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                See How I Work
              </motion.a>
            </div>
          </FadeSlideIn>

          <FadeSlideIn delay={1.8}>
            <div data-testid={TEST_IDS.heroSocialProof}>
              <p className="text-xs uppercase tracking-[0.15em] text-[#2D2D2D]/40 font-sans mb-3">
                Trusted by founders of
              </p>
              <div className="flex flex-wrap gap-x-6 gap-y-2">
                {CLIENT_NAMES.map((name, i) => (
                  <motion.span
                    key={name}
                    className="text-sm font-sans font-medium text-[#1B2A4A]/30 tracking-wide"
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 2.0 + i * 0.1 }}
                    whileHover={{ color: '#C9A96E', y: -2 }}
                  >
                    {name}
                  </motion.span>
                ))}
              </div>
            </div>
          </FadeSlideIn>
        </motion.div>

        {/* Right Image with Parallax */}
        <div className="col-span-1 lg:col-span-5 relative order-1 lg:order-2 h-[35vh] sm:h-[40vh] lg:h-auto overflow-hidden">
          <motion.div
            className="absolute inset-0"
            style={{ y: imageY, scale: imageScale }}
          >
            <motion.img
              src={HERO_IMAGE}
              alt="Sarah Watson with New York City skyline"
              data-testid={TEST_IDS.heroImage}
              className="w-full h-[120%] object-cover object-top"
              loading="eager"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 1.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
            />
          </motion.div>
          {/* Mobile gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#F7F5F2] via-transparent to-transparent lg:hidden opacity-30" />
          {/* Decorative corner accent */}
          <motion.div
            className="absolute bottom-0 left-0 w-24 h-24 border-l-2 border-b-2 border-[#C9A96E]/20 hidden lg:block"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 1.5, duration: 0.8 }}
          />
        </div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2.5, duration: 0.8 }}
        className="absolute bottom-8 left-1/2 -translate-x-1/2 hidden lg:flex flex-col items-center gap-2"
      >
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ArrowDown className="w-4 h-4 text-[#C9A96E]" />
        </motion.div>
      </motion.div>
    </section>
  );
}
